import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AdContentElement } from '@core/models/ad-content-element.model';
import { OrderContactDetails } from '@core/models/order-contact-details.model';
import { Order } from '@core/models/order.model';
import { Style } from '@core/models/style.model';
import { Logger } from '@core/services/logger.service';
import { MultiFieldEditorContent } from '@order/models/multi-field-editor-content';
import { OrderViewModel } from '../models/order-view-model.model';

@Injectable({
	providedIn: 'root'
})
export class EditOrderConversionService {

	private readonly COMPONENT_NAME = "EditOrderConversionService";

	constructor(private logger: Logger) { }

	createOrderContactDetails(form: FormGroup): OrderContactDetails {

		const orderContactDetails = new OrderContactDetails();

		orderContactDetails.email = form.get('email')?.value;
		orderContactDetails.firstName = form.get('firstName')?.value;
		orderContactDetails.lastName = form.get('lastName')?.value;
		orderContactDetails.address1 = form.get('address1')?.value;
		orderContactDetails.address2 = form.get('address2')?.value;
		orderContactDetails.city = form.get('city')?.value;
		orderContactDetails.state = form.get('state')?.value;
		orderContactDetails.country = form.get('country')?.value;
		orderContactDetails.zipcode = form.get('zipcode')?.value;
		orderContactDetails.phone = form.get('phone')?.value;

		return orderContactDetails;
	}

	createMultifieldContentElements(multiFieldEditorContent: MultiFieldEditorContent): AdContentElement[] {

		const adContentElements: AdContentElement[] = [];

		for (const contentViewModel of multiFieldEditorContent.contents) {
			const adContentElement = new AdContentElement();
			adContentElement.key = contentViewModel.key;
			adContentElement.content = contentViewModel.content;
			adContentElements.push(adContentElement);
		}

		return adContentElements;
	}

	createSingleMultifieldContentElementsArray(key: string, content?: string, metadata?: string): AdContentElement[] {

		const adContentElement = new AdContentElement();

		adContentElement.key = key;
		adContentElement.content = content || "";
		adContentElement.metadata = metadata || "";

		return [adContentElement];
	}

	canCalculatePrice(orderViewModel: OrderViewModel): boolean {
		// Calculate Request now requires a style and ad content
		let res = false;

		if (orderViewModel.contentVM && orderViewModel.contentVM.style) {
			if (!this.isContentEmpty(orderViewModel)) {
				res = true;
			}
		}

		return res;
	}

	isContentEmpty(orderViewModel: OrderViewModel): boolean {
		let res = false;
		if (orderViewModel.contentVM.style && this.isStyleMultifield(orderViewModel.contentVM.style)) {
			// multi-field text editor
			if (!orderViewModel.contentVM.multiFieldEditorContent || orderViewModel.contentVM.multiFieldEditorContent.isEmpty) {
				res = true;
			}
		}
		else {
			// ck editor
			if (!orderViewModel.contentVM.adText) {
				res = true;
			}
		}
		return res;
	}

	updateOrderModel(orderViewModel: OrderViewModel, order: Order, updatePreview: boolean): void {
		if (order.orderHeader) {
			orderViewModel.id = order.orderHeader.id || "";
			orderViewModel.adVM.priceNetExTax = order.orderHeader.priceNetExTax || 0;
			orderViewModel.adVM.priceNetIncTax = order.orderHeader.priceNetIncTax || 0;
		}
		else {
			this.logger.error(this.COMPONENT_NAME, "Order has empty orderHeader");
		}

		if (orderViewModel.adVM && orderViewModel.adVM.priceNetIncTax && orderViewModel.adVM.priceNetExTax) {
			orderViewModel.adVM.tax = orderViewModel.adVM.priceNetIncTax - orderViewModel.adVM.priceNetExTax;
		}
		orderViewModel.adVM.currencyCode = order.currencyCode ?? order.currencyCode;

		if (updatePreview) {
			orderViewModel.contentVM.preview = undefined;
			if (order.adContents?.length && order.adContents[0].preview) {
				orderViewModel.contentVM.preview = order.adContents[0].preview;
			}

			// keep the contents in the cache to use for calculatePrice, except preview
			orderViewModel.contentVM.adContentCache = order.adContents;
			if (orderViewModel.contentVM.adContentCache && orderViewModel.contentVM.adContentCache.length > 0) {
				orderViewModel.contentVM.adContentCache[0].preview = undefined;
			}
		}
	}

	// TODO issues dates should not be converted by timezone. They are fixed dates and should have no time component.
	getIssueAsISOString(issue: Date): string {
		const offset = issue.getTimezoneOffset();
		const d = new Date(issue.getTime() - (offset * 60 * 1000));
		const res = d.toISOString().substring(0, 10);
		return res;
	}

	resetPricing(orderViewModel: OrderViewModel): void {
		orderViewModel.isCalculated = false;
		orderViewModel.adVM.priceNetExTax = 0.00;
		orderViewModel.adVM.priceNetIncTax = 0.00;
		orderViewModel.adVM.tax = 0.00;
	}

	resetCache(orderViewModel: OrderViewModel): void {
		//orderViewModel.contentVM.preview = undefined;
		orderViewModel.contentVM.adContentCache = undefined;
	}

	areMandatoryFieldsSet(orderViewModel: OrderViewModel): boolean {
		let res = false;
		// Note: issues now must be selected to run calc/render
		if ((orderViewModel.adVM?.placement) && (orderViewModel.adVM?.package) && (orderViewModel.contentVM?.style) && (orderViewModel.adVM.issueDates?.length > 0)) {
			if (this.isStyleMultifield(orderViewModel.contentVM.style)) {
				// multi-field text style
				if (orderViewModel.contentVM.multiFieldEditorContent && orderViewModel.contentVM.multiFieldEditorContent.isComplete) {
					res = true;
				}
			}
			else {
				// plain/ck text style
				if (orderViewModel.contentVM.adText) {
					res = true;
				}
			}
		}

		return res;
	}

	hasIssueDates(orderViewModel: OrderViewModel): boolean {
		return orderViewModel.adVM.issueDates?.length > 0;
	}

	public isStyleMultifield(style: Style): boolean {
		return style && Array.isArray(style.elements) && style.elements.length > 0;
	}
}
